import intl from "react-intl-universal";

import {ClientInApp} from "../shared/types/SubscriptionTypes";
import {formatDateMonthAsWord, getDaysPassed} from "../utils/date";

import {SUBSCRIPTION_PLANS} from "../checkout/subscription_plans";
import type {CheckoutPeriod} from "../checkout/types";


export const getCurrentPlan = (subscription_plan?: string | null) => {
    if (subscription_plan === 'std') {
        return intl.get('common.subscription.standard').d('Standard');
    } else if (subscription_plan === 'freemium') {
        return intl.get('common.subscription.freemium').d('Freemium');
    } else {
        return intl.get('common.subscription.free_trial').d('Free trial');
    }
}

// freemium or free trial all considered free
export const isFreePlan = (subscription_plan?: string | null) => {
    return subscription_plan !== 'std';
}

// freemium or free trial all considered free
export const isFreemium = (subscription_plan?: string | null) => {
    return subscription_plan === SUBSCRIPTION_PLANS.FREEMIUM;
}

export const isFreeTrial = (client: ClientInApp) => {
    const subscription_plan = client.subscription_plan;
    return subscription_plan === SUBSCRIPTION_PLANS.FREE_TRIAL;
}

function parseDate(value?: string | Date | null): Date | null {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    const normalized = value.trim();
    const timestamp = /^\d+$/.test(normalized) ? Number(normalized) : NaN;
    const date = Number.isFinite(timestamp) ? new Date(timestamp) : new Date(normalized);

    return Number.isNaN(date.getTime()) ? null : date;
}

function getFreeTrialEndDate(client: ClientInApp): Date | null {
    const subscriptionEndsAt = parseDate(client.subscription_endsAt);
    if (subscriptionEndsAt) {
        return subscriptionEndsAt;
    }

    const createdAt = parseDate(client.createdAt);
    if (!createdAt) {
        return null;
    }

    const fallbackEndsAt = new Date(createdAt);
    fallbackEndsAt.setDate(fallbackEndsAt.getDate() + 30);
    return fallbackEndsAt;
}

export function getDaysLeft(clientRow: ClientInApp): number {
    const startDate = parseDate(clientRow.createdAt);
    if (!startDate) {
        return 0;
    }

    const totalDuration = getTotalDuration(clientRow);
    const today = new Date()
    const daysPassed = getDaysPassed(startDate, today);
    return Math.max(0, totalDuration - daysPassed);
}

export const getTotalDuration = (clientRow: ClientInApp) => {
    const startDate = parseDate(clientRow.createdAt);
    const endDate = parseDate(clientRow.subscription_endsAt);

    if (!startDate || !endDate) {
        return 30;
    }

    return getDaysPassed(startDate, endDate);
}


export const isFreeTrialExpired = (client: ClientInApp) => {
    if (!isFreeTrial(client)) {
        return false;
    }

    const subscriptionEndsAt = getFreeTrialEndDate(client);

    return subscriptionEndsAt ? subscriptionEndsAt.getTime() < Date.now() : false;
}

export const getCurrentBillingPeriodText = (client: ClientInApp) => {
    const billing_period = client.subscription_details?.billing_cycle?.interval;
    if (billing_period === 'year') {
        return intl.get('common.subscription.yearly').d('Annual');
    } else if (billing_period === 'month') {
        return intl.get('common.subscription.monthly').d('Monthly');
    } else {
        return '';
    }
}

export const getCurrentCheckoutPeriod = (client: ClientInApp): CheckoutPeriod => {
    if (client?.subscription_status === 'active') {
        const billingInterval = client.subscription_details?.billing_cycle?.interval;
        if (billingInterval === 'year') {
            return 'yearly';
        }
        if (billingInterval === 'month') {
            return 'monthly';
        }
    }

    return 'monthly';
}

export const getNextBillingDate = (client: ClientInApp, currentLocale: string) => {
    const next_billed_at = client.subscription_details?.next_billed_at;
    if (next_billed_at) {
        return formatDateMonthAsWord(currentLocale, new Date(next_billed_at));
    } else {
        return '';
    }
}
